// frontend/src/components/EditAssetModal.js
/**
 * Edit Asset Modal – core fields plus supported category-specific details.
 * Structure matches AssetDetailModal (Section-based layout, same section titles and icons).
 * Styling follows frontend/src/styles/design-system-reference.md.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Package, Shield, CreditCard, Plus, Search, CheckCircle, Settings,
  AlertCircle, Calendar, Car, Eye, EyeOff, FileKey, Fuel, KeyRound,
  FileText, Loader2, Smartphone, Truck, Users,
} from 'lucide-react';
import Modal from './UI/Modal';
import ConfirmModal from './UI/ConfirmModal';
import FormSelect from './UI/FormSelect';
import DatalistCombobox from './UI/DatalistCombobox';
import { useAsset, useUpdateAsset, useVendors, useCreateVendor } from '../hooks/useAssetInventoryData';
import { assetInventoryAPI } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import toast from '../utils/toast';
import { buildUpdatePayload } from '../utils/assetFormPayload';
import { parseApiValidationErrors } from '../utils/apiErrors';
import DatePickerField from './UI/DatePickerField';
import { parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import {
  CONDITION_OPTIONS,
  OWNERSHIP_OPTIONS,
  BRAND_SUGGESTIONS,
  WARRANTY_PROVIDER_SUGGESTIONS,
  SPEC_FIELD_CONFIG,
} from '../constants/assetOptions';

// ============================================
// SECTION & EDIT FIELD (match AssetDetailModal structure)
// ============================================

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-gray-50 rounded-xl p-4 ${className}`}>
    <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
      {Icon && <Icon className="w-4 h-4" />}
      {title}
    </h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
      {children}
    </div>
  </div>
);

const EditField = ({ label, required, children, className = '', hint }) => (
  <div className={className}>
    <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
    <div className="mt-1">{children}</div>
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const CUSTOM_FIELD_LABELS = {
  ram_gb: 'RAM (GB)',
  os_version: 'OS Version',
  storage_gb: 'Storage (GB)',
  cpu: 'CPU',
  size_inches: 'Size (inches)',
  resolution: 'Resolution',
};

const formatCustomFieldLabel = (key, schema) => {
  if (schema?.title) return schema.title;
  if (CUSTOM_FIELD_LABELS[key]) return CUSTOM_FIELD_LABELS[key];
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Access-control lifecycle status (door access / facial recognition).
const ACCESS_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

// Work-use vehicle ownership (fuel cards). Mirrors FuelCard.VehicleOwnership.
const VEHICLE_OWNERSHIP_OPTIONS = [
  { value: '', label: '— Not set —' },
  { value: 'company', label: 'Company Vehicle' },
  { value: 'employee_owned', label: 'Personal Vehicle (used for work)' },
];

const FUEL_TYPE_OPTIONS = [
  { value: '95', label: 'RON 95' },
  { value: '98', label: 'RON 98' },
  { value: '98/95', label: 'RON 95/98' },
  { value: 'diesel', label: 'Diesel' },
  { value: '98/95/D', label: 'RON 95/98 + Diesel' },
  { value: 'all', label: 'All Fuels' },
];

const LICENSE_TYPE_OPTIONS = [
  { value: 'perpetual', label: 'Perpetual' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'concurrent', label: 'Concurrent / Floating' },
  { value: 'named_user', label: 'Named User' },
  { value: 'device', label: 'Device-Based' },
];

const VEHICLE_TYPE_OPTIONS = [
  { value: 'lorry', label: 'Lorry' },
  { value: 'van', label: 'Van' },
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FUEL_FORM = {
  primary_card_number: '',
  secondary_card_number: '',
  pin: '',
  expiry_date: '',
  fuel_type: '98/95',
  monthly_limit: '',
  issue_date: '',
  notes: '',
  vehicle_ownership: '',
  work_use_plate: '',
  work_use_vehicle_desc: '',
  work_use_effective_date: '',
  linked_vehicle: '',
};

const EMPTY_LICENSE_FORM = {
  license_type: 'subscription',
  license_key: '',
  subscription_id: '',
  contract_reference: '',
  total_seats: '1',
  subscription_start_date: '',
  subscription_end_date: '',
  auto_renewal: false,
  publisher: '',
  product_name: '',
  version: '',
  notes: '',
};

const EMPTY_DIESEL_FORM = {
  token_tag: '',
  pin: '',
  linked_vehicle: '',
  notes: '',
};

const EMPTY_FLEET_FORM = {
  vehicle_number: '',
  vehicle_type: 'other',
  iu_number: '',
  registration_date: '',
  chassis_number: '',
  engine_number: '',
  color: '',
  notes: '',
};

const toFuelForm = (fuelCard) => {
  if (!fuelCard) return { ...EMPTY_FUEL_FORM };
  return {
    primary_card_number: fuelCard.primary_card_number ?? '',
    secondary_card_number: fuelCard.secondary_card_number ?? '',
    pin: fuelCard.pin ?? '',
    expiry_date: fuelCard.expiry_date ? String(fuelCard.expiry_date).slice(0, 10) : '',
    fuel_type: fuelCard.fuel_type ?? '98/95',
    monthly_limit: fuelCard.monthly_limit != null ? String(fuelCard.monthly_limit) : '',
    issue_date: fuelCard.issue_date ? String(fuelCard.issue_date).slice(0, 10) : '',
    notes: fuelCard.notes ?? '',
    vehicle_ownership: fuelCard.vehicle_ownership ?? '',
    work_use_plate: fuelCard.work_use_plate ?? '',
    work_use_vehicle_desc: fuelCard.work_use_vehicle_desc ?? '',
    work_use_effective_date: fuelCard.work_use_effective_date
      ? String(fuelCard.work_use_effective_date).slice(0, 10)
      : '',
    linked_vehicle: fuelCard.linked_vehicle != null ? String(fuelCard.linked_vehicle) : '',
  };
};

const toLicenseForm = (license) => {
  if (!license) return { ...EMPTY_LICENSE_FORM };
  return {
    license_type: license.license_type ?? 'subscription',
    license_key: license.license_key ?? '',
    subscription_id: license.subscription_id ?? '',
    contract_reference: license.contract_reference ?? '',
    total_seats: license.total_seats != null ? String(license.total_seats) : '1',
    subscription_start_date: license.subscription_start_date
      ? String(license.subscription_start_date).slice(0, 10)
      : '',
    subscription_end_date: license.subscription_end_date
      ? String(license.subscription_end_date).slice(0, 10)
      : '',
    auto_renewal: Boolean(license.auto_renewal),
    publisher: license.publisher ?? '',
    product_name: license.product_name ?? '',
    version: license.version ?? '',
    notes: license.notes ?? '',
  };
};

const TELCO_OPTIONS = [
  { value: 'm1', label: 'M1' },
  { value: 'singtel', label: 'Singtel' },
  { value: 'zero1', label: 'ZERO1' },
  { value: 'other', label: 'Other' },
];

const EMPTY_MOBILE_FORM = {
  phone_number: '',
  telco: 'other',
  plan_name: '',
  monthly_fee: '',
  bundled_talktime: '',
  bundled_data: '',
  contract_term: '',
  sim_form: '',
  contract_start_date: '',
  contract_end_date: '',
  issued_on: '',
  handset_note: '',
  holder_label: '',
  notes: '',
};

const toMobileForm = (line) => {
  if (!line) return { ...EMPTY_MOBILE_FORM };
  return {
    phone_number: line.phone_number ?? '',
    telco: line.telco || 'other',
    plan_name: line.plan_name ?? '',
    monthly_fee: line.monthly_fee != null ? String(line.monthly_fee) : '',
    bundled_talktime: line.bundled_talktime ?? '',
    bundled_data: line.bundled_data ?? '',
    contract_term: line.contract_term ?? '',
    sim_form: line.sim_form ?? '',
    contract_start_date: line.contract_start_date ? String(line.contract_start_date).slice(0, 10) : '',
    contract_end_date: line.contract_end_date ? String(line.contract_end_date).slice(0, 10) : '',
    issued_on: line.issued_on ? String(line.issued_on).slice(0, 10) : '',
    handset_note: line.handset_note ?? '',
    holder_label: line.holder_label ?? '',
    notes: line.notes ?? '',
  };
};

const toDieselForm = (token) => {
  if (!token) return { ...EMPTY_DIESEL_FORM };
  return {
    token_tag: token.token_tag ?? '',
    pin: token.pin ?? '',
    linked_vehicle: token.linked_vehicle != null ? String(token.linked_vehicle) : '',
    notes: token.notes ?? '',
  };
};

const toFleetForm = (vehicle) => {
  if (!vehicle) return { ...EMPTY_FLEET_FORM };
  return {
    vehicle_number: vehicle.vehicle_number ?? '',
    vehicle_type: vehicle.vehicle_type ?? 'other',
    iu_number: vehicle.iu_number ?? '',
    registration_date: vehicle.registration_date
      ? String(vehicle.registration_date).slice(0, 10)
      : '',
    chassis_number: vehicle.chassis_number ?? '',
    engine_number: vehicle.engine_number ?? '',
    color: vehicle.color ?? '',
    notes: vehicle.notes ?? '',
  };
};

// Categories that do NOT have a meaningful warranty concept and whose
// category-specific data lives on a relational extension model (FleetVehicle,
// SoftwareLicense, FuelCard, DieselToken, AccessControlPass). Most are still
// core-fields-only here; FUEL has a dedicated Work-Use Vehicle editor below.
const CATEGORIES_WITHOUT_CORE_WARRANTY = new Set(['FLT', 'LIC', 'FUEL', 'DSL', 'ACC', 'TEL']);
const CATEGORIES_REQUIRING_EXTENSION = new Set(['FLT', 'LIC', 'FUEL', 'DSL', 'TEL']);

// Mirrors AssetDetailModal.AccessControlSection: an access pass is "Deactivated"
// when status is 'deactivated' OR a deactivation date is recorded.
const deriveAccessStatus = (asset) =>
  (asset?.status === 'deactivated' || !!asset?.deactivation_date) ? 'deactivated' : 'active';

const EMPTY_FORM = {
  asset_tag: '',
  serial_number: '',
  brand: '',
  model: '',
  description: '',
  condition: 'good',
  location: '',
  remarks: '',
  purchase_date: '',
  purchase_price: '',
  vendor: '',
  purchase_vendor: '',
  purchase_order_number: '',
  invoice_number: '',
  ownership_type: 'owned',
  warranty_start_date: '',
  warranty_end_date: '',
  warranty_provider: '',
  warranty_terms: '',
  custom_fields: {},
  // Access-control lifecycle (only edited for ACC assets)
  access_status: 'active',
  activation_date: '',
  deactivation_date: '',
};

const toFormState = (asset) => {
  if (!asset) return { ...EMPTY_FORM };
  return {
    asset_tag: asset.asset_tag ?? '',
    serial_number: asset.serial_number ?? '',
    brand: asset.brand ?? '',
    model: asset.model ?? '',
    description: asset.description ?? '',
    condition: asset.condition ?? 'good',
    location: asset.location ?? '',
    remarks: asset.remarks ?? '',
    purchase_date: asset.purchase_date ? String(asset.purchase_date).slice(0, 10) : '',
    purchase_price: asset.purchase_price != null ? String(asset.purchase_price) : '',
    vendor: asset.vendor_detail?.id != null ? String(asset.vendor_detail.id) : '',
    purchase_vendor: asset.purchase_vendor ?? '',
    purchase_order_number: asset.purchase_order_number ?? '',
    invoice_number: asset.invoice_number ?? '',
    ownership_type: asset.ownership_type ?? 'owned',
    warranty_start_date: asset.warranty_start_date ? String(asset.warranty_start_date).slice(0, 10) : '',
    warranty_end_date: asset.warranty_end_date ? String(asset.warranty_end_date).slice(0, 10) : '',
    warranty_provider: asset.warranty_provider ?? '',
    warranty_terms: asset.warranty_terms ?? '',
    custom_fields: asset.custom_fields && typeof asset.custom_fields === 'object' ? { ...asset.custom_fields } : {},
    access_status: deriveAccessStatus(asset),
    activation_date: asset.activation_date ? String(asset.activation_date).slice(0, 10) : '',
    deactivation_date: asset.deactivation_date ? String(asset.deactivation_date).slice(0, 10) : '',
  };
};

const EditAssetModal = ({ isOpen, onClose, asset }) => {
  const queryClient = useQueryClient();
  const assetId = asset?.id;
  const { data: fetchedAsset, isLoading, isError, error: fetchError } = useAsset(assetId);

  const [formState, setFormState] = useState(toFormState(null));
  const [initialFormState, setInitialFormState] = useState(toFormState(null));
  const [fuelForm, setFuelForm] = useState({ ...EMPTY_FUEL_FORM });
  const [initialFuelForm, setInitialFuelForm] = useState({ ...EMPTY_FUEL_FORM });
  const [licenseForm, setLicenseForm] = useState({ ...EMPTY_LICENSE_FORM });
  const [initialLicenseForm, setInitialLicenseForm] = useState({ ...EMPTY_LICENSE_FORM });
  const [dieselForm, setDieselForm] = useState({ ...EMPTY_DIESEL_FORM });
  const [initialDieselForm, setInitialDieselForm] = useState({ ...EMPTY_DIESEL_FORM });
  const [fleetForm, setFleetForm] = useState({ ...EMPTY_FLEET_FORM });
  const [initialFleetForm, setInitialFleetForm] = useState({ ...EMPTY_FLEET_FORM });
  const [mobileForm, setMobileForm] = useState({ ...EMPTY_MOBILE_FORM });
  const [initialMobileForm, setInitialMobileForm] = useState({ ...EMPTY_MOBILE_FORM });
  const [showFuelPin, setShowFuelPin] = useState(false);
  const [showDieselPin, setShowDieselPin] = useState(false);
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [fleetLoaded, setFleetLoaded] = useState(false);
  const [savingExtension, setSavingExtension] = useState(false);
  const updateAsset = useUpdateAsset();
  const { data: vendors = [] } = useVendors();
  const createVendor = useCreateVendor();
  const [showNewVendor, setShowNewVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorUen, setNewVendorUen] = useState('');
  const [uenLookupResult, setUenLookupResult] = useState(null);
  const [uenLookupLoading, setUenLookupLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Refetch when opening so we don't edit from stale cache
  useEffect(() => {
    if (isOpen && assetId) {
      queryClient.invalidateQueries({ queryKey: ['assets', 'detail', assetId] });
    }
  }, [isOpen, assetId, queryClient]);

  // Initialise form from fetched asset; reset when asset id changes
  useEffect(() => {
    if (fetchedAsset && assetId === fetchedAsset.id) {
      const nextFormState = toFormState(fetchedAsset);
      setFormState(nextFormState);
      setInitialFormState(nextFormState);
      const ff = toFuelForm(fetchedAsset.fuel_card);
      setFuelForm(ff);
      setInitialFuelForm(ff);
      const lf = toLicenseForm(fetchedAsset.software_license);
      setLicenseForm(lf);
      setInitialLicenseForm(lf);
      const df = toDieselForm(fetchedAsset.diesel_token);
      setDieselForm(df);
      setInitialDieselForm(df);
      const vf = toFleetForm(fetchedAsset.fleet_vehicle);
      setFleetForm(vf);
      setInitialFleetForm(vf);
      const mf = toMobileForm(fetchedAsset.mobile_line);
      setMobileForm(mf);
      setInitialMobileForm(mf);
      setShowFuelPin(false);
      setShowDieselPin(false);
      setSubmitError('');
      setShowDiscardConfirm(false);
    }
  }, [assetId, fetchedAsset]);

  // Lazily load fleet vehicles only for an editor that needs a vehicle picker,
  // so ordinary asset edits never fetch the full fleet.
  useEffect(() => {
    const needsFuelVehicle = fuelForm.vehicle_ownership === 'company';
    const needsDieselVehicle = Boolean(fetchedAsset?.diesel_token);
    if ((!needsFuelVehicle && !needsDieselVehicle) || fleetLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await assetInventoryAPI.getFleetVehicles({ page_size: 500 });
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        setFleetVehicles(list);
        setFleetLoaded(true);
      } catch {
        if (!cancelled) setFleetLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchedAsset?.diesel_token, fuelForm.vehicle_ownership, fleetLoaded]);

  const setField = useCallback((name, value) => {
    setSubmitError('');
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setFuelField = useCallback((name, value) => {
    setSubmitError('');
    setFuelForm((prev) => {
      const next = { ...prev, [name]: value };
      // Keep the work-use declaration internally consistent (mirrors the
      // FuelCard.clean() / serializer.validate() rules):
      if (name === 'vehicle_ownership' && value !== 'company') {
        next.linked_vehicle = '';
      }
      return next;
    });
  }, []);

  const setLicenseField = useCallback((name, value) => {
    setSubmitError('');
    setLicenseForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setDieselField = useCallback((name, value) => {
    setSubmitError('');
    setDieselForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setFleetField = useCallback((name, value) => {
    setSubmitError('');
    setFleetForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setMobileField = useCallback((name, value) => {
    setSubmitError('');
    setMobileForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setCustomField = useCallback((key, value) => {
    setSubmitError('');
    setFormState((prev) => ({
      ...prev,
      custom_fields: { ...prev.custom_fields, [key]: value },
    }));
  }, []);

  const schemaProperties = useMemo(() => {
    const props = fetchedAsset?.asset_type_detail?.custom_fields_schema?.properties;
    if (!props || typeof props !== 'object') return null;
    const entries = Object.entries(props);
    return entries.length > 0 ? entries : null;
  }, [fetchedAsset]);

  const vendorOptions = useMemo(
    () =>
      (Array.isArray(vendors) ? vendors : []).map((v) => ({
        value: String(v.id),
        label: `${v.name}${v.uen ? ` (${v.uen})` : ''}`,
      })),
    [vendors]
  );

  const warrantyProviderSuggestions = useMemo(() => {
    const vendorNames = (Array.isArray(vendors) ? vendors : []).map((v) => v.name);
    return [...WARRANTY_PROVIDER_SUGGESTIONS, ...vendorNames];
  }, [vendors]);

  const fleetVehicleOptions = useMemo(
    () => [
      { value: '', label: '— Not in fleet register / enter plate below —' },
      ...fleetVehicles.map((v) => ({
        value: String(v.id),
        label: `${v.vehicle_number}${
          v.brand || v.vehicle_model ? ` — ${[v.brand, v.vehicle_model].filter(Boolean).join(' ')}` : ''
        }`,
      })),
    ],
    [fleetVehicles]
  );
  const dieselVehicleOptions = useMemo(
    () => [
      { value: '', label: '— Not linked —' },
      ...fleetVehicles.map((vehicle) => ({
        value: String(vehicle.id),
        label: `${vehicle.vehicle_number}${
          vehicle.brand || vehicle.vehicle_model
            ? ` — ${[vehicle.brand, vehicle.vehicle_model].filter(Boolean).join(' ')}`
            : ''
        }`,
      })),
    ],
    [fleetVehicles]
  );

  const categoryCode = fetchedAsset?.category_code || asset?.category_code;
  const assetTypeDetail = fetchedAsset?.asset_type_detail;
  const isFuelCard = Boolean(fetchedAsset?.fuel_card) || categoryCode === 'FUEL';
  const isFleetVehicle = Boolean(fetchedAsset?.fleet_vehicle) || categoryCode === 'FLT';
  const isSoftwareLicense = Boolean(fetchedAsset?.software_license) || categoryCode === 'LIC';
  const isDieselToken = Boolean(fetchedAsset?.diesel_token) || categoryCode === 'DSL';
  const isMobileLine = Boolean(fetchedAsset?.mobile_line) || categoryCode === 'TEL';
  const isAccessControl = fetchedAsset?.category_code === 'ACC' || fetchedAsset?.is_access_control_asset === true;
  const isPhysicalHandover = fetchedAsset?.requires_physical_handover !== false;
  const fuelCardId = fetchedAsset?.fuel_card?.id;
  const licenseId = fetchedAsset?.software_license?.id;
  const dieselTokenId = fetchedAsset?.diesel_token?.id;
  const fleetVehicleId = fetchedAsset?.fleet_vehicle?.id;
  const mobileLineId = fetchedAsset?.mobile_line?.id;
  const hasFuelEditor = Boolean(isFuelCard && fuelCardId);
  const hasLicenseEditor = Boolean(isSoftwareLicense && licenseId);
  const hasDieselEditor = Boolean(isDieselToken && dieselTokenId);
  const hasFleetEditor = Boolean(isFleetVehicle && fleetVehicleId);
  const hasMobileEditor = Boolean(isMobileLine && mobileLineId);
  const hasCategoryEditor = Boolean(
    hasFuelEditor
    || hasLicenseEditor
    || hasDieselEditor
    || hasFleetEditor
    || hasMobileEditor
    || isAccessControl
  );
  const isMissingCategoryEditor = (
    CATEGORIES_REQUIRING_EXTENSION.has(categoryCode)
    && !hasCategoryEditor
  );
  const showSerialNumber = (
    assetTypeDetail?.requires_serial_number !== false
    && !isFuelCard
    && !isSoftwareLicense
    && !isDieselToken
  );
  const showBrandAndModel = (
    !isFuelCard
    && !isSoftwareLicense
    && !isDieselToken
    && !isAccessControl
    && !isMobileLine
  );
  const showConditionAndLocation = (
    isPhysicalHandover
    && !isFuelCard
    && !isSoftwareLicense
    && !isDieselToken
    && !isMobileLine
  );
  const showPurchaseInformation = (
    assetTypeDetail?.requires_purchase_date !== false
    && !isFuelCard
    && !isDieselToken
    && !isAccessControl
    && !isFleetVehicle
    && !isMobileLine
  );
  const showWarrantyInformation = (
    !CATEGORIES_WITHOUT_CORE_WARRANTY.has(categoryCode)
    && Number(assetTypeDetail?.default_warranty_years ?? 0) > 0
  );
  const coreSectionTitle = isSoftwareLicense
    ? 'License Record'
    : isDieselToken
      ? 'Token Record'
      : isFleetVehicle
        ? 'Vehicle Record'
        : isAccessControl
          ? 'Access Record'
          : isMobileLine
            ? 'SIM Record'
            : 'Asset Details';

  // Status drives the deactivation date so the two never disagree (the detail
  // view derives "Deactivated" from either). Active clears the date; Deactivated
  // defaults it to today when not already set.
  const setAccessStatus = useCallback((value) => {
    setSubmitError('');
    setFormState((prev) => {
      if (value === 'active') {
        return { ...prev, access_status: 'active', deactivation_date: '' };
      }
      return {
        ...prev,
        access_status: 'deactivated',
        deactivation_date: prev.deactivation_date || toLocalISODate(new Date()),
      };
    });
  }, []);
  const fuelFormDirty = useMemo(
    () => JSON.stringify(fuelForm) !== JSON.stringify(initialFuelForm),
    [fuelForm, initialFuelForm]
  );
  const licenseFormDirty = useMemo(
    () => JSON.stringify(licenseForm) !== JSON.stringify(initialLicenseForm),
    [licenseForm, initialLicenseForm]
  );
  const dieselFormDirty = useMemo(
    () => JSON.stringify(dieselForm) !== JSON.stringify(initialDieselForm),
    [dieselForm, initialDieselForm]
  );
  const fleetFormDirty = useMemo(
    () => JSON.stringify(fleetForm) !== JSON.stringify(initialFleetForm),
    [fleetForm, initialFleetForm]
  );
  const mobileFormDirty = useMemo(
    () => JSON.stringify(mobileForm) !== JSON.stringify(initialMobileForm),
    [mobileForm, initialMobileForm]
  );
  const coreFormDirty = useMemo(
    () => JSON.stringify(formState) !== JSON.stringify(initialFormState),
    [formState, initialFormState]
  );
  const hasExtensionChanges = (
    fuelFormDirty
    || licenseFormDirty
    || dieselFormDirty
    || fleetFormDirty
    || mobileFormDirty
  );
  const hasAssetChanges = coreFormDirty || hasExtensionChanges;
  const hasVendorDraft = showNewVendor && Boolean(
    newVendorName.trim() || newVendorUen.trim()
  );
  const hasUnsavedChanges = hasAssetChanges || hasVendorDraft;
  const isSaving = updateAsset.isPending || savingExtension || createVendor.isPending;

  const requestClose = useCallback(() => {
    if (isSaving) return;
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  }, [hasUnsavedChanges, isSaving, onClose]);

  const buildFuelPayload = () => {
    const ownership = fuelForm.vehicle_ownership || '';
    const linkedVehicle = ownership === 'company' && fuelForm.linked_vehicle
      ? Number(fuelForm.linked_vehicle)
      : null;
    return {
      primary_card_number: fuelForm.primary_card_number.trim(),
      secondary_card_number: fuelForm.secondary_card_number.trim(),
      pin: fuelForm.pin.trim(),
      expiry_date: fuelForm.expiry_date || null,
      fuel_type: fuelForm.fuel_type,
      monthly_limit: fuelForm.monthly_limit === '' ? null : fuelForm.monthly_limit,
      issue_date: fuelForm.issue_date || null,
      notes: fuelForm.notes.trim(),
      vehicle_ownership: ownership,
      // When a company fleet vehicle is linked, the backend syncs the plate
      // from it; we still send the typed plate for personal / unlisted vehicles.
      work_use_plate: linkedVehicle ? '' : (fuelForm.work_use_plate || '').trim(),
      work_use_vehicle_desc: (fuelForm.work_use_vehicle_desc || '').trim(),
      work_use_effective_date: fuelForm.work_use_effective_date || null,
      linked_vehicle: linkedVehicle,
    };
  };

  const buildLicensePayload = () => ({
    license_type: licenseForm.license_type,
    license_key: licenseForm.license_key.trim(),
    subscription_id: licenseForm.subscription_id.trim(),
    contract_reference: licenseForm.contract_reference.trim(),
    total_seats: Number(licenseForm.total_seats),
    subscription_start_date: licenseForm.subscription_start_date || null,
    subscription_end_date: licenseForm.subscription_end_date || null,
    auto_renewal: licenseForm.auto_renewal,
    publisher: licenseForm.publisher.trim(),
    product_name: licenseForm.product_name.trim(),
    version: licenseForm.version.trim(),
    notes: licenseForm.notes.trim(),
  });

  const buildDieselPayload = () => ({
    token_tag: dieselForm.token_tag.trim(),
    pin: dieselForm.pin.trim(),
    linked_vehicle: dieselForm.linked_vehicle ? Number(dieselForm.linked_vehicle) : null,
    notes: dieselForm.notes.trim(),
  });

  const buildFleetPayload = () => ({
    vehicle_number: fleetForm.vehicle_number.trim().toUpperCase(),
    vehicle_type: fleetForm.vehicle_type,
    iu_number: fleetForm.iu_number.trim(),
    registration_date: fleetForm.registration_date || null,
    chassis_number: fleetForm.chassis_number.trim().toUpperCase(),
    engine_number: fleetForm.engine_number.trim().toUpperCase(),
    color: fleetForm.color.trim(),
    notes: fleetForm.notes.trim(),
  });

  const optionalDate = (value) => value || null;
  const buildMobilePayload = () => ({
    phone_number: mobileForm.phone_number.trim(),
    telco: mobileForm.telco,
    plan_name: mobileForm.plan_name.trim(),
    monthly_fee: mobileForm.monthly_fee === '' ? null : mobileForm.monthly_fee,
    bundled_talktime: mobileForm.bundled_talktime.trim(),
    bundled_data: mobileForm.bundled_data.trim(),
    contract_term: mobileForm.contract_term.trim(),
    sim_form: mobileForm.sim_form.trim(),
    contract_start_date: optionalDate(mobileForm.contract_start_date),
    contract_end_date: optionalDate(mobileForm.contract_end_date),
    issued_on: optionalDate(mobileForm.issued_on),
    handset_note: mobileForm.handset_note.trim(),
    holder_label: mobileForm.holder_label.trim(),
    notes: mobileForm.notes.trim(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!assetId || isSaving || !hasAssetChanges) return;
    setSubmitError('');
    const tag = (formState.asset_tag || '').trim();
    if (!tag) {
      setSubmitError('Asset tag is required.');
      toast.error('Asset tag is required');
      return;
    }

    const data = buildUpdatePayload(formState);

    // Access-control lifecycle: only sent for ACC assets (backend rejects these
    // fields for other categories). Keep status/deactivation_date consistent.
    if (isAccessControl) {
      const deactivated = formState.access_status === 'deactivated';
      data.status = deactivated ? 'deactivated' : 'active';
      data.activation_date = formState.activation_date || null;
      data.deactivation_date = deactivated ? (formState.deactivation_date || null) : null;
    }

    if (coreFormDirty) {
      try {
        await updateAsset.mutateAsync({
          id: assetId,
          data,
          showSuccessToast: !hasExtensionChanges,
        });
        setInitialFormState(formState);
      } catch (err) {
        const message = parseApiValidationErrors(err)
          || 'The asset could not be updated. Review the fields and try again.';
        setSubmitError(message);
        if (err?.response?.status === 404) {
          toast.error('This asset was deleted');
          onClose();
        }
        return;
      }
    }

    let extensionUpdate = null;
    if (isFuelCard && fuelCardId && fuelFormDirty) {
      extensionUpdate = {
        save: () => assetInventoryAPI.updateFuelCard(fuelCardId, buildFuelPayload()),
        markSaved: () => setInitialFuelForm(fuelForm),
        label: 'fuel card',
      };
    } else if (hasLicenseEditor && licenseFormDirty) {
      extensionUpdate = {
        save: () => assetInventoryAPI.updateLicense(licenseId, buildLicensePayload()),
        markSaved: () => setInitialLicenseForm(licenseForm),
        label: 'software license',
      };
    } else if (hasDieselEditor && dieselFormDirty) {
      extensionUpdate = {
        save: () => assetInventoryAPI.updateDieselToken(dieselTokenId, buildDieselPayload()),
        markSaved: () => setInitialDieselForm(dieselForm),
        label: 'diesel token',
      };
    } else if (hasFleetEditor && fleetFormDirty) {
      extensionUpdate = {
        save: () => assetInventoryAPI.updateFleetVehicle(fleetVehicleId, buildFleetPayload()),
        markSaved: () => setInitialFleetForm(fleetForm),
        label: 'vehicle',
      };
    } else if (hasMobileEditor && mobileFormDirty) {
      extensionUpdate = {
        save: () => assetInventoryAPI.updateMobileLine(mobileLineId, buildMobilePayload()),
        markSaved: () => setInitialMobileForm(mobileForm),
        label: 'mobile line',
      };
    }

    // Persist category-specific fields on their authoritative extension model;
    // the core Asset PATCH deliberately does not duplicate these values.
    if (extensionUpdate) {
      setSavingExtension(true);
      try {
        await extensionUpdate.save();
        extensionUpdate.markSaved();
        queryClient.invalidateQueries({ queryKey: ['assets'] });
        toast.success(
          coreFormDirty
            ? `Asset and ${extensionUpdate.label} details updated`
            : `${extensionUpdate.label.charAt(0).toUpperCase()}${extensionUpdate.label.slice(1)} details updated`
        );
      } catch (err) {
        const message = parseApiValidationErrors(err)
          || `The ${extensionUpdate.label} details could not be updated. Review the fields and try again.`;
        const displayMessage = coreFormDirty
          ? `Asset fields were saved, but ${extensionUpdate.label} details were not: ${message}`
          : message;
        setSubmitError(displayMessage);
        toast.error(displayMessage);
        setSavingExtension(false);
        return; // keep modal open so the user can correct category-specific fields
      }
      setSavingExtension(false);
    }

    onClose();
  };

  const handleUenLookup = async () => {
    const uen = newVendorUen.trim().toUpperCase();
    if (!uen || uen.length < 8) {
      toast.error('Enter a valid UEN (8+ characters)');
      return;
    }
    setUenLookupLoading(true);
    try {
      const { data } = await assetInventoryAPI.verifyVendorUEN(uen);
      setUenLookupResult(data);
      if (data.already_exists) {
        setField('vendor', String(data.vendor.id));
        setShowNewVendor(false);
        toast.success(`Vendor already exists: ${data.vendor.name}`);
      } else if (data.verified) {
        setNewVendorName(data.entity_name);
        toast.success(`ACRA: ${data.entity_name}`);
      } else {
        toast.warning('UEN not found in ACRA – you can still add manually', { icon: '⚠️' });
      }
    } catch {
      toast.error('UEN lookup failed');
    } finally {
      setUenLookupLoading(false);
    }
  };

  const handleCreateVendor = () => {
    const name = newVendorName.trim();
    if (!name) { toast.error('Vendor name is required'); return; }
    createVendor.mutate(
      { name, uen: newVendorUen.trim().toUpperCase() || null },
      {
        onSuccess: (created) => {
          setField('vendor', String(created.id));
          setShowNewVendor(false);
          setNewVendorName('');
          setNewVendorUen('');
          setUenLookupResult(null);
          toast.success(`Vendor created: ${created.name}`);
        },
      }
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        title={isMobileLine ? 'Edit mobile line' : 'Edit Asset'}
        size="lg"
        theme="admin"
        closable={!isSaving}
        footer={
          <>
            <button
              type="button"
              onClick={requestClose}
              disabled={isSaving}
              className="btn btn-admin-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-asset-form"
              disabled={isLoading || isSaving || isError || !hasAssetChanges}
              className="btn btn-admin-primary"
            >
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
              {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </>
        }
      >
        <form id="edit-asset-form" onSubmit={handleSubmit} className="space-y-4">
              {submitError && !isLoading && !isError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{submitError}</span>
                </div>
              )}
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-16" />
                          <div className="h-10 bg-gray-200 rounded w-full" />
                        </div>
                        <div className="space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-16" />
                          <div className="h-10 bg-gray-200 rounded w-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : isError ? (
                <div className="text-center py-6">
                  <p className="text-sm text-red-600 mb-2">
                    {fetchError?.response?.status === 403
                      ? 'You can only edit assets in categories assigned to you.'
                      : fetchError?.response?.status === 404
                      ? 'This asset was not found or has been deleted.'
                      : 'Failed to load asset. Please try again.'}
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn btn-admin-outline"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {hasFuelEditor ? (
                    <Section title="Card Details" icon={CreditCard}>
                      <EditField
                        label="Card ID"
                        required
                        hint="Internal asset identifier used throughout the portal."
                      >
                        <input
                          type="text"
                          value={formState.asset_tag}
                          onChange={(e) => setField('asset_tag', e.target.value)}
                          className="input-admin w-full"
                          placeholder="e.g. D02"
                        />
                      </EditField>
                      <EditField label="Account number" required>
                        <input
                          type="text"
                          value={fuelForm.primary_card_number}
                          onChange={(e) => setFuelField('primary_card_number', e.target.value)}
                          className="input-admin w-full"
                          autoComplete="off"
                        />
                      </EditField>
                      <EditField label="Card number" required>
                        <input
                          type="text"
                          value={fuelForm.secondary_card_number}
                          onChange={(e) => setFuelField('secondary_card_number', e.target.value)}
                          className="input-admin w-full"
                          autoComplete="off"
                        />
                      </EditField>
                      <EditField label="PIN">
                        <div className="relative">
                          <input
                            type={showFuelPin ? 'text' : 'password'}
                            value={fuelForm.pin}
                            onChange={(e) => setFuelField('pin', e.target.value)}
                            className="input-admin w-full pr-11"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowFuelPin((visible) => !visible)}
                            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                            aria-label={showFuelPin ? 'Hide fuel card PIN' : 'Show fuel card PIN'}
                            title={showFuelPin ? 'Hide PIN' : 'Show PIN'}
                          >
                            {showFuelPin
                              ? <EyeOff className="w-4 h-4" aria-hidden="true" />
                              : <Eye className="w-4 h-4" aria-hidden="true" />}
                          </button>
                        </div>
                      </EditField>
                      <EditField label="Fuel type">
                        <FormSelect
                          theme="admin"
                          options={FUEL_TYPE_OPTIONS}
                          value={fuelForm.fuel_type}
                          onChange={(value) => setFuelField('fuel_type', value)}
                          placeholder="Select allowed fuel"
                        />
                      </EditField>
                      <EditField label="Monthly limit" hint="Leave blank when the card has no fixed limit.">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={fuelForm.monthly_limit}
                          onChange={(e) => setFuelField('monthly_limit', e.target.value)}
                          className="input-admin w-full"
                          placeholder="No limit"
                        />
                      </EditField>
                      <EditField label="Notes" className="sm:col-span-2">
                        <textarea
                          value={fuelForm.notes}
                          onChange={(e) => setFuelField('notes', e.target.value)}
                          className="textarea-admin w-full"
                          rows={2}
                          placeholder="Operational notes for this fuel card"
                        />
                      </EditField>
                    </Section>
                  ) : hasLicenseEditor ? (
                    <Section title="License Details" icon={FileKey}>
                      <EditField label="Asset tag" required>
                        <input
                          type="text"
                          value={formState.asset_tag}
                          onChange={(e) => setField('asset_tag', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Product" required>
                        <input
                          type="text"
                          value={licenseForm.product_name}
                          onChange={(e) => setLicenseField('product_name', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Publisher">
                        <input
                          type="text"
                          value={licenseForm.publisher}
                          onChange={(e) => setLicenseField('publisher', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Version">
                        <input
                          type="text"
                          value={licenseForm.version}
                          onChange={(e) => setLicenseField('version', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="License type">
                        <FormSelect
                          theme="admin"
                          options={LICENSE_TYPE_OPTIONS}
                          value={licenseForm.license_type}
                          onChange={(value) => setLicenseField('license_type', value)}
                          placeholder="Select license type"
                        />
                      </EditField>
                      <EditField label="Subscription ID">
                        <input
                          type="text"
                          value={licenseForm.subscription_id}
                          onChange={(e) => setLicenseField('subscription_id', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Contract reference">
                        <input
                          type="text"
                          value={licenseForm.contract_reference}
                          onChange={(e) => setLicenseField('contract_reference', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="License key" className="sm:col-span-2">
                        <input
                          type="text"
                          value={licenseForm.license_key}
                          onChange={(e) => setLicenseField('license_key', e.target.value)}
                          className="input-admin w-full font-mono"
                          autoComplete="off"
                        />
                      </EditField>
                      <EditField label="Notes" className="sm:col-span-2">
                        <textarea
                          value={licenseForm.notes}
                          onChange={(e) => setLicenseField('notes', e.target.value)}
                          className="textarea-admin w-full"
                          rows={2}
                        />
                      </EditField>
                    </Section>
                  ) : hasDieselEditor ? (
                    <Section title="Token Details" icon={Fuel}>
                      <EditField label="Asset tag" required>
                        <input
                          type="text"
                          value={formState.asset_tag}
                          onChange={(e) => setField('asset_tag', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Token number" required>
                        <input
                          type="text"
                          value={dieselForm.token_tag}
                          onChange={(e) => setDieselField('token_tag', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="PIN">
                        <div className="relative">
                          <input
                            type={showDieselPin ? 'text' : 'password'}
                            value={dieselForm.pin}
                            onChange={(e) => setDieselField('pin', e.target.value)}
                            className="input-admin w-full pr-11"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowDieselPin((visible) => !visible)}
                            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-gray-400 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-red-500"
                            aria-label={showDieselPin ? 'Hide diesel token PIN' : 'Show diesel token PIN'}
                            title={showDieselPin ? 'Hide PIN' : 'Show PIN'}
                          >
                            {showDieselPin
                              ? <EyeOff className="w-4 h-4" aria-hidden="true" />
                              : <Eye className="w-4 h-4" aria-hidden="true" />}
                          </button>
                        </div>
                      </EditField>
                      <EditField label="Linked vehicle">
                        <FormSelect
                          theme="admin"
                          options={dieselVehicleOptions}
                          value={dieselForm.linked_vehicle}
                          onChange={(value) => setDieselField('linked_vehicle', value)}
                          placeholder="— Select vehicle —"
                        />
                      </EditField>
                      <EditField label="Notes" className="sm:col-span-2">
                        <textarea
                          value={dieselForm.notes}
                          onChange={(e) => setDieselField('notes', e.target.value)}
                          className="textarea-admin w-full"
                          rows={2}
                        />
                      </EditField>
                    </Section>
                  ) : hasFleetEditor ? (
                    <Section title="Vehicle Identity" icon={Truck}>
                      <EditField label="Asset tag" required>
                        <input
                          type="text"
                          value={formState.asset_tag}
                          onChange={(e) => setField('asset_tag', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Vehicle number" required>
                        <input
                          type="text"
                          value={fleetForm.vehicle_number}
                          onChange={(e) => setFleetField('vehicle_number', e.target.value.toUpperCase())}
                          className="input-admin w-full"
                          placeholder="e.g. GBE9491G"
                        />
                      </EditField>
                      <EditField label="Vehicle type">
                        <FormSelect
                          theme="admin"
                          options={VEHICLE_TYPE_OPTIONS}
                          value={fleetForm.vehicle_type}
                          onChange={(value) => setFleetField('vehicle_type', value)}
                          placeholder="Select vehicle type"
                        />
                      </EditField>
                      <EditField label="Brand">
                        <DatalistCombobox
                          value={formState.brand}
                          onChange={(value) => setField('brand', value)}
                          suggestions={BRAND_SUGGESTIONS}
                          placeholder="Select or type a brand"
                        />
                      </EditField>
                      <EditField label="Model">
                        <input
                          type="text"
                          value={formState.model}
                          onChange={(e) => setField('model', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Registration date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(fleetForm.registration_date)}
                          onChange={(date) => setFleetField('registration_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select registration date"
                          isClearable
                        />
                      </EditField>
                      <EditField label="Chassis / VIN">
                        <input
                          type="text"
                          value={fleetForm.chassis_number}
                          onChange={(e) => setFleetField('chassis_number', e.target.value.toUpperCase())}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Engine number">
                        <input
                          type="text"
                          value={fleetForm.engine_number}
                          onChange={(e) => setFleetField('engine_number', e.target.value.toUpperCase())}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="IU number">
                        <input
                          type="text"
                          value={fleetForm.iu_number}
                          onChange={(e) => setFleetField('iu_number', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Colour">
                        <input
                          type="text"
                          value={fleetForm.color}
                          onChange={(e) => setFleetField('color', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Notes" className="sm:col-span-2">
                        <textarea
                          value={fleetForm.notes}
                          onChange={(e) => setFleetField('notes', e.target.value)}
                          className="textarea-admin w-full"
                          rows={2}
                        />
                      </EditField>
                    </Section>
                  ) : hasMobileEditor ? (
                    <>
                      <Section title="Line" icon={Smartphone}>
                        <EditField
                          label="Number"
                          required
                          hint="This is the line. Saving updates the asset tag to match, for example SIM-81007835."
                        >
                          <input
                            type="text"
                            inputMode="numeric"
                            value={mobileForm.phone_number}
                            onChange={(e) => setMobileField('phone_number', e.target.value)}
                            className="input-admin w-full"
                            placeholder="81007835"
                          />
                        </EditField>
                        <EditField label="Telco" required>
                          <FormSelect
                            options={TELCO_OPTIONS}
                            value={mobileForm.telco}
                            onChange={(value) => setMobileField('telco', value)}
                            theme="admin"
                          />
                        </EditField>
                        <EditField label="Plan" className="sm:col-span-2">
                          <input
                            type="text"
                            value={mobileForm.plan_name}
                            onChange={(e) => setMobileField('plan_name', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                        <EditField
                          label="Monthly fee"
                          hint="SGD per month. This is the subscription, not a purchase price."
                        >
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={mobileForm.monthly_fee}
                            onChange={(e) => setMobileField('monthly_fee', e.target.value)}
                            className="input-admin w-full"
                            placeholder="0.00"
                          />
                        </EditField>
                        <EditField label="Data">
                          <input
                            type="text"
                            value={mobileForm.bundled_data}
                            onChange={(e) => setMobileField('bundled_data', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                        <EditField label="Talktime">
                          <input
                            type="text"
                            value={mobileForm.bundled_talktime}
                            onChange={(e) => setMobileField('bundled_talktime', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                        <EditField label="SIM form">
                          <input
                            type="text"
                            value={mobileForm.sim_form}
                            onChange={(e) => setMobileField('sim_form', e.target.value)}
                            className="input-admin w-full"
                            placeholder="e.g. Nano SIM, 5G"
                          />
                        </EditField>
                        <EditField label="ICCID" hint="Printed on the SIM. Leave blank if the register has no serial.">
                          <input
                            type="text"
                            value={formState.serial_number}
                            onChange={(e) => setField('serial_number', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                        <EditField label="Handset note" className="sm:col-span-2" hint="The phone recorded against this line. It is not a separate asset.">
                          <input
                            type="text"
                            value={mobileForm.handset_note}
                            onChange={(e) => setMobileField('handset_note', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                      </Section>
                      <Section title="Lock-in" icon={Calendar}>
                        <EditField label="Term">
                          <input
                            type="text"
                            value={mobileForm.contract_term}
                            onChange={(e) => setMobileField('contract_term', e.target.value)}
                            className="input-admin w-full"
                            placeholder="e.g. 12 mths"
                          />
                        </EditField>
                        <EditField label="Lock-in starts">
                          <DatePickerField
                            theme="admin"
                            selected={parseLocalDate(mobileForm.contract_start_date)}
                            onChange={(date) => setMobileField('contract_start_date', date ? toLocalISODate(date) : '')}
                            placeholderText="Select date"
                            isClearable
                          />
                        </EditField>
                        <EditField
                          label="Lock-in ends"
                          hint="End of the minimum term. A past date means the line is still billed. Clear it only when the register has no date."
                        >
                          <DatePickerField
                            theme="admin"
                            selected={parseLocalDate(mobileForm.contract_end_date)}
                            onChange={(date) => setMobileField('contract_end_date', date ? toLocalISODate(date) : '')}
                            placeholderText="No date on record"
                            isClearable
                          />
                        </EditField>
                        <EditField label="Issued">
                          <DatePickerField
                            theme="admin"
                            selected={parseLocalDate(mobileForm.issued_on)}
                            onChange={(date) => setMobileField('issued_on', date ? toLocalISODate(date) : '')}
                            placeholderText="Select date"
                            isClearable
                          />
                        </EditField>
                      </Section>
                      <Section title="Register" icon={FileText}>
                        <EditField
                          label="Holder on register"
                          className="sm:col-span-2"
                          hint={fetchedAsset?.mobile_line?.holder_kind === 'equipment'
                            ? 'Where the SIM sits, such as a router or a site table. Assign still names the staff member who is accountable. Their Work Phone stays unchanged.'
                            : 'The name on the master list. To put the SIM on a person\'s My Assets, use Assign. Typing a name here does not do that.'}
                        >
                          <input
                            type="text"
                            value={mobileForm.holder_label}
                            onChange={(e) => setMobileField('holder_label', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                        <EditField
                          label="Register notes"
                          className="sm:col-span-2"
                          hint="Port history, site use, or a note such as how long the line stays in service."
                        >
                          <textarea
                            value={formState.remarks}
                            onChange={(e) => setField('remarks', e.target.value)}
                            className="textarea-admin w-full"
                            rows={3}
                          />
                        </EditField>
                      </Section>
                    </>
                  ) : (
                    <Section title={coreSectionTitle} icon={Package}>
                      <EditField label="Asset tag" required>
                        <input
                          type="text"
                          value={formState.asset_tag}
                          onChange={(e) => setField('asset_tag', e.target.value)}
                          className="input-admin w-full"
                          placeholder={isMobileLine ? 'SIM-81007835' : 'e.g. WL-20230703'}
                        />
                      </EditField>
                      {showSerialNumber && (
                        <EditField label={isMobileLine ? 'SIM serial (ICCID)' : 'Serial number'}>
                          <input
                            type="text"
                            value={formState.serial_number}
                            onChange={(e) => setField('serial_number', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                      )}
                      {showBrandAndModel && (
                        <>
                          <EditField label="Brand">
                            <DatalistCombobox
                              value={formState.brand}
                              onChange={(val) => setField('brand', val)}
                              suggestions={BRAND_SUGGESTIONS}
                              placeholder="Select or type a brand"
                            />
                          </EditField>
                          <EditField label="Model">
                            <input
                              type="text"
                              value={formState.model}
                              onChange={(e) => setField('model', e.target.value)}
                              className="input-admin w-full"
                            />
                          </EditField>
                        </>
                      )}
                      {showConditionAndLocation && (
                        <>
                          <EditField label="Condition">
                            <FormSelect
                              theme="admin"
                              options={CONDITION_OPTIONS}
                              value={formState.condition}
                              onChange={(val) => setField('condition', val)}
                              placeholder="Select condition"
                            />
                          </EditField>
                          <EditField label="Location">
                            <input
                              type="text"
                              value={formState.location}
                              onChange={(e) => setField('location', e.target.value)}
                              className="input-admin w-full"
                              placeholder="Physical location if not assigned"
                            />
                          </EditField>
                        </>
                      )}
                      {!isMobileLine && (
                        <EditField label="Description" className="sm:col-span-2">
                          <input
                            type="text"
                            value={formState.description}
                            onChange={(e) => setField('description', e.target.value)}
                            className="input-admin w-full"
                          />
                        </EditField>
                      )}
                      <EditField label="Remarks" className="sm:col-span-2">
                        <input
                          type="text"
                          value={formState.remarks}
                          onChange={(e) => setField('remarks', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                    </Section>
                  )}

                  {/* Card validity belongs to the FuelCard extension. Custodians
                      edit it here without needing to understand the storage split. */}
                  {hasFuelEditor && (
                    <Section title="Card Validity" icon={Calendar}>
                      <EditField
                        label="Expiry date"
                        hint="Date printed on the card. Used for renewal planning and expiry alerts."
                      >
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(fuelForm.expiry_date)}
                          onChange={(date) => setFuelField('expiry_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select expiry date"
                          isClearable
                        />
                      </EditField>
                      <EditField
                        label="Issue date"
                        hint="Original date the card became available for use."
                      >
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(fuelForm.issue_date)}
                          onChange={(date) => setFuelField('issue_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select issue date"
                          isClearable
                        />
                      </EditField>
                    </Section>
                  )}

                  {hasLicenseEditor && (
                    <Section title="Subscription & Seats" icon={Users}>
                      <EditField label="Start date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(licenseForm.subscription_start_date)}
                          onChange={(date) => setLicenseField(
                            'subscription_start_date',
                            date ? toLocalISODate(date) : ''
                          )}
                          placeholderText="Select start date"
                          isClearable
                        />
                      </EditField>
                      <EditField label="End date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(licenseForm.subscription_end_date)}
                          onChange={(date) => setLicenseField(
                            'subscription_end_date',
                            date ? toLocalISODate(date) : ''
                          )}
                          placeholderText="Select end date"
                          isClearable
                        />
                      </EditField>
                      <EditField
                        label="Total seats"
                        hint="Assigned and available counts are calculated automatically."
                      >
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={licenseForm.total_seats}
                          onChange={(e) => setLicenseField('total_seats', e.target.value)}
                          className="input-admin w-full"
                        />
                      </EditField>
                      <EditField label="Auto-renewal">
                        <label className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-full border border-gray-300 bg-white px-4 text-sm text-gray-700 shadow-sm transition-[border-color,box-shadow] duration-200 hover:bg-gray-50 focus-within:border-red-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-red-500">
                          <input
                            type="checkbox"
                            checked={licenseForm.auto_renewal}
                            onChange={(e) => setLicenseField('auto_renewal', e.target.checked)}
                            className="h-4 w-4 shrink-0 rounded border-gray-300 accent-red-600 text-red-600 focus-visible:outline-none"
                          />
                          Enabled
                        </label>
                      </EditField>
                    </Section>
                  )}

                  {/* Access Details – lifecycle for access-control credentials
                      (door access / facial recognition). This is what an ops
                      manager actually manages: is access live, and the
                      grant/revoke dates. Mirrors AssetDetailModal's view. */}
                  {isAccessControl && (
                    <Section title="Access Details" icon={KeyRound}>
                      <EditField label="Access status">
                        <FormSelect
                          theme="admin"
                          options={ACCESS_STATUS_OPTIONS}
                          value={formState.access_status}
                          onChange={(val) => setAccessStatus(val)}
                          placeholder="Select status"
                        />
                      </EditField>
                      <EditField label="Activation date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(formState.activation_date)}
                          onChange={(date) => setField('activation_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select date"
                          isClearable
                        />
                      </EditField>
                      <EditField
                        label="Deactivation date"
                        className="sm:col-span-2"
                        hint={
                          formState.access_status === 'deactivated'
                            ? 'Date access was revoked (e.g. at offboarding).'
                            : 'Set the status to Deactivated to record a revocation date.'
                        }
                      >
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(formState.deactivation_date)}
                          onChange={(date) => setField('deactivation_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select date"
                          isClearable
                          disabled={formState.access_status !== 'deactivated'}
                        />
                      </EditField>
                    </Section>
                  )}

                  {schemaProperties && !isFuelCard && !hasLicenseEditor && !hasDieselEditor && !hasFleetEditor && !hasMobileEditor && (
                    <Section title="Specifications" icon={Settings}>
                      {schemaProperties.map(([key, schemaDef]) => {
                        const hasEnum = Array.isArray(schemaDef.enum) && schemaDef.enum.length > 0;
                        const isNumeric = schemaDef.type === 'integer' || schemaDef.type === 'number';
                        const fieldConfig = SPEC_FIELD_CONFIG[key];
                        const currentValue = formState.custom_fields[key] ?? '';

                        // Schema-defined enum always wins → closed dropdown.
                        const selectOptions = hasEnum
                          ? schemaDef.enum.map((opt) => ({
                              value: String(opt),
                              label: isNumeric && opt >= 1024 ? `${opt / 1024} TB` : String(opt),
                            }))
                          : fieldConfig?.kind === 'select'
                            ? fieldConfig.options
                            : null;

                        const isCombobox = !hasEnum && fieldConfig?.kind === 'combobox';

                        return (
                          <EditField key={key} label={formatCustomFieldLabel(key, schemaDef)}>
                            {selectOptions ? (
                              <FormSelect
                                theme="admin"
                                options={selectOptions}
                                value={currentValue === '' ? '' : String(currentValue)}
                                onChange={(val) => setCustomField(key, val)}
                                placeholder="— Select —"
                              />
                            ) : isCombobox ? (
                              <DatalistCombobox
                                value={currentValue}
                                onChange={(val) => setCustomField(key, val)}
                                suggestions={fieldConfig.suggestions}
                                placeholder={`Select or type ${formatCustomFieldLabel(key, schemaDef).toLowerCase()}`}
                              />
                            ) : (
                              <input
                                type={isNumeric ? 'number' : 'text'}
                                step={schemaDef.type === 'number' ? 'any' : schemaDef.type === 'integer' ? '1' : undefined}
                                min={isNumeric ? '0' : undefined}
                                value={currentValue}
                                onChange={(e) => setCustomField(key, e.target.value)}
                                className="input-admin w-full"
                              />
                            )}
                          </EditField>
                        );
                      })}
                    </Section>
                  )}

                  {/* Warranty – hidden for categories that don't have one
                      (vehicles use COE/road tax/insurance; licenses use
                      subscription periods; fuel/diesel/access cards have no
                      warranty concept). */}
                  {showWarrantyInformation && (
                    <Section title="Warranty" icon={Shield}>
                      <EditField label="Start date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(formState.warranty_start_date)}
                          onChange={(date) => setField('warranty_start_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select date"
                          isClearable
                        />
                      </EditField>
                      <EditField label="End date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(formState.warranty_end_date)}
                          onChange={(date) => setField('warranty_end_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select date"
                          isClearable
                        />
                      </EditField>
                      <EditField label="Provider">
                        <DatalistCombobox
                          value={formState.warranty_provider}
                          onChange={(val) => setField('warranty_provider', val)}
                          suggestions={warrantyProviderSuggestions}
                          placeholder="Select or type a provider"
                        />
                      </EditField>
                      <EditField label="Terms" className="sm:col-span-2">
                        <textarea
                          value={formState.warranty_terms}
                          onChange={(e) => setField('warranty_terms', e.target.value)}
                          className="textarea-admin w-full"
                          rows={2}
                        />
                      </EditField>
                    </Section>
                  )}

                  {/* Purchase Information – same section as View (ITEquipmentSection / DefaultAssetSection).
                      Hidden for access-control credentials, which are not procured
                      as physical goods (no price / vendor / PO / ownership). */}
                  {showPurchaseInformation && (
                  <Section title="Purchase Information" icon={CreditCard}>
                    <EditField label="Date">
                      <DatePickerField
                        theme="admin"
                        selected={parseLocalDate(formState.purchase_date)}
                        onChange={(date) => setField('purchase_date', date ? toLocalISODate(date) : '')}
                        placeholderText="Select date"
                        isClearable
                      />
                    </EditField>
                    <EditField label="Price">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formState.purchase_price}
                        onChange={(e) => setField('purchase_price', e.target.value)}
                        className="input-admin w-full"
                        placeholder="0.00"
                      />
                    </EditField>
                    <EditField label="Vendor" className="sm:col-span-2">
                      {!showNewVendor ? (
                        <div className="flex gap-2 items-center">
                          <div className="flex-1">
                            <FormSelect
                              theme="admin"
                              options={vendorOptions}
                              value={formState.vendor}
                              onChange={(val) => setField('vendor', val)}
                              placeholder="— Select vendor —"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowNewVendor(true)}
                            className="btn btn-admin-outline text-xs flex gap-1 whitespace-nowrap"
                          >
                            <Plus className="w-3.5 h-3.5" /> New
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2 p-3 bg-white border border-gray-200 rounded-lg">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="UEN (optional)"
                              value={newVendorUen}
                              onChange={(e) => setNewVendorUen(e.target.value)}
                              className="input-admin flex-1"
                            />
                            <button
                              type="button"
                              onClick={handleUenLookup}
                              disabled={uenLookupLoading || !newVendorUen.trim()}
                              className="btn btn-admin-outline text-xs flex gap-1"
                            >
                              <Search className="w-3.5 h-3.5" />
                              {uenLookupLoading ? 'Looking up…' : 'Verify'}
                            </button>
                          </div>
                          {uenLookupResult?.verified && !uenLookupResult?.already_exists && (
                            <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 px-2 py-1 rounded">
                              <CheckCircle className="w-3.5 h-3.5" />
                              ACRA: {uenLookupResult.entity_name}
                            </div>
                          )}
                          <input
                            type="text"
                            placeholder="Vendor name *"
                            value={newVendorName}
                            onChange={(e) => setNewVendorName(e.target.value)}
                            className="input-admin w-full"
                          />
                          <div className="flex gap-2 justify-end">
                            <button
                              type="button"
                              onClick={() => { setShowNewVendor(false); setNewVendorName(''); setNewVendorUen(''); setUenLookupResult(null); }}
                              className="btn btn-admin-outline text-xs"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateVendor}
                              disabled={createVendor.isPending || !newVendorName.trim()}
                              className="btn btn-admin-primary text-xs"
                            >
                              {createVendor.isPending ? 'Creating…' : 'Add Vendor'}
                            </button>
                          </div>
                        </div>
                      )}
                    </EditField>
                    <EditField label="PO number">
                      <input
                        type="text"
                        value={formState.purchase_order_number}
                        onChange={(e) => setField('purchase_order_number', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Invoice">
                      <input
                        type="text"
                        value={formState.invoice_number}
                        onChange={(e) => setField('invoice_number', e.target.value)}
                        className="input-admin w-full"
                      />
                    </EditField>
                    <EditField label="Ownership">
                      <FormSelect
                        theme="admin"
                        options={OWNERSHIP_OPTIONS}
                        value={formState.ownership_type}
                        onChange={(val) => setField('ownership_type', val)}
                        placeholder="Select ownership"
                      />
                    </EditField>
                  </Section>
                  )}

                  {/* Work-Use Vehicle – fuel-card audit justification (the vehicle
                      this card fuels; company fleet link or personal car). */}
                  {hasFuelEditor && (
                    <Section title="Work-Use Vehicle" icon={Car}>
                      <EditField label="Ownership" className="sm:col-span-2">
                        <FormSelect
                          theme="admin"
                          options={VEHICLE_OWNERSHIP_OPTIONS}
                          value={fuelForm.vehicle_ownership}
                          onChange={(val) => setFuelField('vehicle_ownership', val)}
                          placeholder="— Not set —"
                        />
                      </EditField>

                      {fuelForm.vehicle_ownership === 'company' && (
                        <EditField label="Fleet vehicle" className="sm:col-span-2">
                          <FormSelect
                            theme="admin"
                            options={fleetVehicleOptions}
                            value={fuelForm.linked_vehicle}
                            onChange={(val) => setFuelField('linked_vehicle', val)}
                            placeholder="— Not in fleet register / enter plate below —"
                          />
                        </EditField>
                      )}

                      <EditField label="Plate">
                        <input
                          type="text"
                          value={
                            fuelForm.vehicle_ownership === 'company' && fuelForm.linked_vehicle
                              ? (fleetVehicles.find((v) => String(v.id) === String(fuelForm.linked_vehicle))?.vehicle_number || '')
                              : fuelForm.work_use_plate
                          }
                          onChange={(e) => setFuelField('work_use_plate', e.target.value.toUpperCase())}
                          disabled={fuelForm.vehicle_ownership === 'company' && !!fuelForm.linked_vehicle}
                          className="input-admin w-full disabled:bg-gray-100 disabled:text-gray-500"
                          placeholder="e.g. SNC1694J"
                        />
                      </EditField>
                      <EditField label="Effective date">
                        <DatePickerField
                          theme="admin"
                          selected={parseLocalDate(fuelForm.work_use_effective_date)}
                          onChange={(date) => setFuelField('work_use_effective_date', date ? toLocalISODate(date) : '')}
                          placeholderText="Select date"
                          isClearable
                        />
                      </EditField>
                      <EditField label="Vehicle make / model" className="sm:col-span-2">
                        <input
                          type="text"
                          value={fuelForm.work_use_vehicle_desc}
                          onChange={(e) => setFuelField('work_use_vehicle_desc', e.target.value)}
                          className="input-admin w-full"
                          placeholder="e.g. Honda Civic (for personal vehicles not in the fleet register)"
                        />
                      </EditField>
                    </Section>
                  )}

                  {isMissingCategoryEditor && (
                    <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="font-medium text-amber-900">Core fields only.</span>{' '}
                        This record is missing its category-specific data extension, so only
                        the base asset fields are available. Ask an administrator to repair
                        the asset record before changing operational details.
                      </div>
                    </div>
                  )}
                </div>
              )}
        </form>
      </Modal>

      <ConfirmModal
        isOpen={showDiscardConfirm}
        title="Discard unsaved changes?"
        message="Your asset updates have not been saved. Discard your changes?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        variant="danger"
        theme="admin"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  );
};

export default EditAssetModal;
